import { Injectable } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { Observable, forkJoin } from 'rxjs'
import { first } from 'rxjs/operators'

import { Persona } from 'app/models'

import { Mapper } from './mapper.service'
import { UserPersonaService } from './user-persona.service'
import { ScrollHelperService } from './scroll-helper.service'

@Injectable({ providedIn: 'root' })
export class PersonaSaverService {

  public submitting = false
  public error: string

  constructor(
    private userPersonaService: UserPersonaService,
    private scrollHelperService: ScrollHelperService
  ) { }

  public savePersona(persona: Persona, next: (saved: boolean) => void) {
    this.submitting = true
    this.error = undefined
    this.userPersonaService.updatePersona(persona)
      .pipe(first())
      .subscribe(
        (data: any) => {
          const newPersona = Mapper.as<Persona>(data.result, Persona)
          this.userPersonaService.reloadPersona(persona, newPersona)
          this.submitting = false
          next(true)
        },
        (err: HttpErrorResponse) => {
          this.error = 'There was an error.'
          if (err.error && err.error.error && err.error.error.messages) {
            this.error += ' ' + err.error.error.messages.join(', ')
          }
          this.submitting = false
          this.scrollHelperService.scrollTo(0, 0)
        }
      )
  }

  public saveSelfPersona(contextPersona: Persona, selfPersona: Persona, next: (saved: boolean) => void) {
    this.submitting = true
    const childUpdater: any = this.saveChildPersonaAttributes(contextPersona, selfPersona)
    const stream: any[] = [
      this.userPersonaService.updatePersona(selfPersona).pipe(first())
    ]
    if (childUpdater) stream.push(childUpdater)
    forkJoin(stream)
      .subscribe((data: any[]) => {
          const newPersona = Mapper.as<Persona>(data[0].result, Persona)
          this.userPersonaService.reloadPersona(selfPersona, newPersona)
          this.submitting = false
          next(true)
        },
        (err: HttpErrorResponse) => {
          this.submitting = false
          this.error = 'There was an error saving your account'
          this.scrollHelperService.scrollTo(0, 0)
        })
  }

  public saveChildPersonaAttributes(child: Persona, parent: Persona): Observable<any> | undefined {
    if (child.id === parent.id) return undefined

    const addrsMatch: boolean = this.objectsMatch(child.primaryAddress, parent.primaryAddress)
    const phonesMatch: boolean = child.primaryPhone === parent.primaryPhone
    if (addrsMatch && phonesMatch) return undefined

    child.primaryAddressData = {
      country: parent.primaryAddress.country,
      street_1: parent.primaryAddress.street_1,
      city: parent.primaryAddress.city,
      state_or_province: parent.primaryAddress.state_or_province,
      zip: parent.primaryAddress.zip
    }
    child.primaryPhone = parent.primaryPhone

    this.submitting = true
    return this.userPersonaService.updatePersona(child).pipe(first())
  }

  private objectsMatch(item1: any, item2: any): boolean {
    return JSON.stringify(item1) === JSON.stringify(item2)
  }
}
